import type { CrossLink, Edges, NodeId, NodeMap } from './types';

/**
 * The wire layer, as arithmetic.
 *
 * `drawWires()` re-measures the DOM with two `querySelector` calls and a forced reflow PER
 * EDGE, and it runs on every `pointermove` frame of a drag. But look at what it actually
 * uses: `x`, `y`, `w`, `h`, `side` and `color` — every one of which is already a value in
 * the position map or the size store. So none of the measuring was ever necessary; it was
 * only the shortest way to reach numbers the model had mislaid.
 *
 * This module is that same arithmetic with the numbers passed in. It touches no DOM, which
 * is what lets a drag repaint the wires by rewriting `d` attributes on the same frame — no
 * reflow, no React render — and what lets the curve maths be unit-tested at all.
 *
 * THE CURVE MATHS IS PORTED CHARACTER FOR CHARACTER, deliberately, including the parts that
 * look like accidents:
 *
 * - Three different fallback sizes appear in the original for an unmeasured node: 150x42 in
 *   `anchor()`, and 44 in the root-parent branch of `drawWires()` that recomputes the height
 *   itself. They are preserved separately below rather than unified, because unifying them
 *   moves wires by a pixel on any node that is not yet measured.
 * - `anchor()` returns a DIFFERENT SHAPE for the root — `{x, y}` rather than
 *   `{left, right, cy}` — so `ap.cy` is `undefined` for a root parent. Both call sites
 *   special-case the root before reading it, and the cross-link path leans on it a third way
 *   (`aa.cy || (posY(a) + 21)`). Reproduced as written.
 * - Coordinates are interpolated at full double precision, exactly as the template literals
 *   did. Only `linkPath`'s control point is rounded, and only to one decimal, because that is
 *   what the original rounds.
 */

/** What `anchor()` answers for an ordinary node. */
export interface NodeAnchor {
  left: number;
  right: number;
  cy: number;
}

export interface WireGeometry {
  /** Canonical x and the side the node is laid out on. */
  positionOf: (id: NodeId) => { x: number; side: number } | undefined;
  /** Render-time y — canonical, or the display pass's while the doc rows are expanded. */
  yOf: (id: NodeId) => number;
  /** Measured box. Absent for a node that has never been rendered. */
  sizeOf: (id: NodeId) => { w: number; h: number } | undefined;
}

/**
 * `anchor()`, for a node that is not the root.
 *
 * `side` is passed RAW — possibly undefined — because the original tests `n.side >= 0`, which
 * is FALSE for undefined, while the caller that picks the x coordinate tests `(n.side || 0)
 * >= 0`, which is TRUE. The two disagree for a node with no side at all, and the disagreement
 * is reproduced rather than tidied: tidying it moves that node's wire.
 */
export function nodeAnchor(id: NodeId, side: number | undefined, g: WireGeometry): NodeAnchor {
  const size = g.sizeOf(id);
  const w = size ? size.w : 150;
  const h = size ? size.h : 42;
  const x = g.positionOf(id)?.x ?? 0;
  const y = g.yOf(id);
  if ((side as number) >= 0) return { left: x, right: x + w, cy: y + h / 2 };
  return { left: x - w, right: x, cy: y + h / 2 };
}

/**
 * A curved (quadratic) path between two points — bowed perpendicular to the straight line so
 * a dotted cross-link arcs rather than cutting across the nodes between its ends.
 *
 * A heuristic, and the original says so: it reduces overlaps and reads better, but it is not
 * obstacle-avoidance routing.
 */
export function linkPath(p1: { x: number; y: number }, p2: { x: number; y: number }): string {
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const len = Math.hypot(dx, dy) || 1;
  const bow = Math.min(len * 0.22, 140); // arc height, capped
  const cx = mx + (-dy / len) * bow;
  const cy = my + (dx / len) * bow; // control point offset perpendicular
  return `M${p1.x},${p1.y} Q${cx.toFixed(1)},${cy.toFixed(1)} ${p2.x},${p2.y}`;
}

export interface WireInput extends WireGeometry {
  nodes: NodeMap;
  edges: Edges;
  links: readonly CrossLink[];
  isHidden: (id: NodeId) => boolean;
  showLinks: boolean;
  /** Edit mode adds a fat transparent hit-path under each cross-link. */
  editing: boolean;
  isLight: boolean;
}

/** One parent-child wire. */
export interface EdgeWire {
  /** The CHILD id: an edge is identified by its child, because `edges` is inverted. */
  id: NodeId;
  d: string;
  stroke: string;
  opacity: number;
}

/** One dotted cross-link. `index` is its position in `links`, which the menu addresses it by. */
export interface LinkWire {
  index: number;
  d: string;
  stroke: string;
}

export interface Wires {
  edges: EdgeWire[];
  links: LinkWire[];
}

/**
 * Every path the wire layer draws, in the order the original emitted them.
 *
 * Order is not cosmetic: cross-links paint over parent-child wires because they come second,
 * and the hit-path must sit immediately before its own visible link — `#wires .xlink-hit:hover
 * + .xlink` is an adjacent-sibling rule and thickens the wrong line otherwise.
 */
export function computeWires(input: WireInput): Wires {
  const { nodes, edges, links } = input;
  const out: Wires = { edges: [], links: [] };

  for (const child in edges) {
    const parentId = edges[child];
    const nc = nodes[child];
    const np = nodes[parentId];
    if (!nc || !np) continue;
    if (input.isHidden(child) || input.isHidden(parentId)) continue;

    const side = nc.side || 0;
    const ac = nodeAnchor(child, nc.side, input);

    let x1: number;
    let y1: number;
    if (np.root) {
      // The root's own branch: its x is the centre line, and it recomputes the height with a
      // fallback of 44 rather than `anchor()`'s 42. Kept separate on purpose.
      const size = input.sizeOf(parentId);
      x1 = input.positionOf(parentId)?.x ?? 0;
      y1 = input.yOf(parentId) + (size ? size.h : 44) / 2;
    } else {
      const ap = nodeAnchor(parentId, np.side, input);
      x1 = side >= 0 ? ap.right : ap.left;
      y1 = ap.cy;
    }
    const x2 = side >= 0 ? ac.left : ac.right;
    const y2 = ac.cy;
    const mx = (x1 + x2) / 2;

    out.edges.push({
      id: child,
      d: `M${x1},${y1} C${mx},${y1} ${mx},${y2} ${x2},${y2}`,
      stroke: nc.color || '#8b6bff',
      opacity: input.isLight ? 0.75 : 0.85,
    });
  }

  if (input.showLinks) {
    links.forEach((lk, i) => {
      const a = lk[0];
      const b = lk[1];
      const col = typeof lk[2] === 'string' && lk[2] ? lk[2] : 'var(--accent)';
      const na = nodes[a];
      const nb = nodes[b];
      if (!na || !nb) return;
      if (input.isHidden(a) || input.isHidden(b)) return;

      const p1 = endpointOf(a, na.root, na.side, input);
      const p2 = endpointOf(b, nb.root, nb.side, input);
      out.links.push({ index: i, d: linkPath(p1, p2), stroke: col });
    });
  }

  return out;
}

/**
 * Where a cross-link attaches.
 *
 * The root is the special case twice over: its x is the centre line rather than an edge of
 * its box, and `anchor()` gives it no `cy` at all — which is why the original falls back to
 * `posY(id) + 21`, half of the 42px fallback height, whatever the root is actually measuring.
 */
function endpointOf(
  id: NodeId,
  isRoot: boolean | undefined,
  side: number | undefined,
  g: WireInput,
): { x: number; y: number } {
  // The root has no `cy` in the original at all — `anchor()` returns a different shape for it
  // — so the `||` fallback below is the ONLY branch it ever takes, and the 21 is half of the
  // 42px fallback height rather than half of whatever the root actually measures.
  if (isRoot) return { x: g.positionOf(id)?.x ?? 0, y: g.yOf(id) + 21 };
  const a = nodeAnchor(id, side, g);
  // `||` rather than `??`, as the original has it: a cy of exactly zero falls back too.
  const y = a.cy || g.yOf(id) + 21;
  return { x: (side as number) >= 0 ? a.right : a.left, y };
}
